import { gql } from "@apollo/client";

// Presigned URL을 요청하는 GraphQL 쿼리
const GET_PRESIGNED_URL = gql`
  query getUploadPresignedUrl($input: GetPresignedUrlInput!) {
    getUploadPresignedUrl(input: $input) {
      url
      key
    }
  }
`;

/**
 * Presigned URL을 이용해 S3에 파일을 업로드하는 유틸리티 함수
 * @param {object} client - Apollo Client 인스턴스
 * @param {File} file - 업로드할 파일 객체
 * @returns {Promise<string>} - 업로드 완료 후 최종 파일 URL
 */
export const uploadFileWithPresignedUrl = async (client, file) => {
  if (!file) {
    throw new Error("업로드할 파일이 선택되지 않았습니다.");
  }

  try {
    // 1. 백엔드에서 Presigned URL 요청
    console.log("Requesting Presigned URL for:", file.name);
    const { data: presignedData } = await client.query({
      query: GET_PRESIGNED_URL,
      variables: {
        input: {
          filename: file.name,
          contentType: file.type || "application/octet-stream",
        },
      },
      fetchPolicy: "no-cache",
    });

    const { url: presignedUrl, key: objectKey } =
      presignedData.getUploadPresignedUrl;

    if (!presignedUrl) {
      throw new Error("Presigned URL을 받아오지 못했습니다.");
    }

    // 2. 받은 Presigned URL로 파일 업로드 (PUT)
    console.log("Uploading to Presigned URL...");
    const response = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `파일 업로드 실패: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    // 3. 업로드 성공 후 최종 URL 구성 및 반환
    const s3BucketUrl = `https://${process.env.REACT_APP_AWS_S3_BUCKET}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com`;
    const finalFileUrl = `${s3BucketUrl}/${objectKey}`;

    console.log("Upload successful. Final URL:", finalFileUrl);
    return finalFileUrl;
  } catch (error) {
    console.error("Error during the upload process:", error);
    // 에러를 다시 throw하여 호출한 쪽에서 처리할 수 있도록 함
    throw error;
  }
};
